import { CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Table as AntTable, Button, Card, Flex, Tag, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { PayrollShiftListItemDto } from '@clh/api-client';
import { HumlaDateTime, toHourAndMinuteDisplay } from '@clh/util';

import { useApiClient } from '../hooks/use-api-client';
import Spinner from '../spinner';

import usePayPeriodContext from './pay-period-context';
import {
    filterShifts,
    isAwaitingTimeSubmission,
    ShiftCountFilter,
} from './shift-filters';
import {
    findPayPeriod,
    nurseNameSortValue,
    scheduledMinutesSortValue,
    submittedMinutesSortValue,
} from './shift-sorters';

const { Text } = Typography;

const filterDescriptions: Record<ShiftCountFilter, string> = {
    awaiting: 'shifts awaiting time submission',
    active: 'assigned & active shifts',
};

function SummaryCount({
    count,
    label,
    color,
    selected,
    onClick,
}: {
    count: number;
    label: string;
    color: string;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <Button
            color="primary"
            variant={selected ? 'outlined' : 'text'}
            disabled={count === 0}
            aria-pressed={selected}
            onClick={onClick}
            style={{ height: 'auto', flexDirection: 'column', gap: 0 }}
        >
            <div style={{ fontSize: '20px', fontWeight: 'bold', color }}>
                {count}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
                {label}
            </Text>
        </Button>
    );
}

function ShiftSummary({
    shifts,
    selected,
    onSelect,
}: {
    shifts: PayrollShiftListItemDto[];
    selected: ShiftCountFilter | null;
    onSelect: (filter: ShiftCountFilter | null) => void;
}) {
    const totalShifts = shifts.length;
    const shiftsAwaitingTimeSubmission = shifts.filter(
        isAwaitingTimeSubmission
    ).length;

    const toggle = (filter: ShiftCountFilter) =>
        onSelect(selected === filter ? null : filter);

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
                <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
                    Shift Summary
                </Typography.Title>
                <Flex gap={24}>
                    {shiftsAwaitingTimeSubmission > 0 && (
                        <SummaryCount
                            count={shiftsAwaitingTimeSubmission}
                            label="Awaiting Time Submission"
                            color="red"
                            selected={selected === 'awaiting'}
                            onClick={() => toggle('awaiting')}
                        />
                    )}
                    <SummaryCount
                        count={totalShifts}
                        label="Assigned & Active Shifts"
                        color="#52c41a"
                        selected={selected === 'active'}
                        onClick={() => toggle('active')}
                    />
                </Flex>
            </Flex>
        </Card>
    );
}

export default function PayrollShifts() {
    const api = useApiClient();
    const [shifts, setShifts] = useState<PayrollShiftListItemDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [countFilter, setCountFilter] = useState<ShiftCountFilter | null>(
        null
    );

    const { startDate: payPeriodStart, endDate: payPeriodEnd } =
        usePayPeriodContext();

    const fetchShifts = async () => {
        if (!api) return;

        setLoading(true);
        try {
            const response = await api.payrollControllerListPayrollShifts({
                after: payPeriodStart,
                before: payPeriodEnd,
            });

            setShifts(response);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCountFilter(null);
        void fetchShifts();
    }, [payPeriodStart.toISOString(), payPeriodEnd.toISOString()]);

    if (!api) {
        return <Spinner />;
    }

    if (loading) {
        return <Spinner />;
    }

    const payPeriodForShift = (shift: PayrollShiftListItemDto) =>
        findPayPeriod(shift, payPeriodStart, payPeriodEnd);

    const columns = [
        {
            title: 'Facility',
            dataIndex: 'facility',
            render: (facility: any, shift: PayrollShiftListItemDto) => (
                <Button
                    type="link"
                    target="_blank"
                    href={`/shifts/${shift.id}`}
                    icon={<LinkOutlined />}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {facility.name}
                </Button>
            ),
            ellipsis: true,
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                a.facility.name.localeCompare(b.facility.name),
        },
        {
            dataIndex: 'shiftType',
            render: (shiftType: string, shift: PayrollShiftListItemDto) => (
                <>
                    <Tag>{shiftType.toLowerCase()}</Tag>
                    {shift.specialtyCode && <Tag>{shift.specialtyCode}</Tag>}
                </>
            ),
        },
        {
            title: 'Scheduled Time',
            dataIndex: 'startTime',
            width: 320,
            render: (_: any, shift: PayrollShiftListItemDto) => {
                const tz = shift.facility?.timezone;
                const start = DateTime.fromJSDate(shift.startTime);
                const end = DateTime.fromJSDate(shift.endTime);
                return (
                    <Typography.Text type="secondary">
                        {(tz ? start.setZone(tz) : start).toLocaleString(
                            HumlaDateTime.DATETIME_SHORT
                        )}{' '}
                        -{' '}
                        {(tz ? end.setZone(tz) : end).toLocaleString(
                            tz
                                ? HumlaDateTime.DATETIME_SHORT_WITH_TZ
                                : HumlaDateTime.DATETIME_SHORT
                        )}
                    </Typography.Text>
                );
            },
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                a.startTime.getTime() - b.startTime.getTime(),
        },
        {
            title: 'Assigned Nurse',
            dataIndex: 'nurseProfile',
            render: (
                nurseProfile: PayrollShiftListItemDto['nurseProfile'] | null
            ) =>
                nurseProfile && (
                    <Button
                        type="link"
                        target="_blank"
                        href={`/profiles/${nurseProfile.id}`}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        {nurseProfile.firstName} {nurseProfile.lastName}
                    </Button>
                ),
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                nurseNameSortValue(a).localeCompare(nurseNameSortValue(b)),
        },
        {
            title: 'Hours Submitted',
            dataIndex: 'minutesWorked',
            width: 140,
            render: (
                minutesWorked: number | null,
                shift: PayrollShiftListItemDto
            ) => {
                const payPeriod = payPeriodForShift(shift);

                if (shift.didNotWork) {
                    return <b>Did not work</b>;
                }
                if (!minutesWorked && !shift.onCallShift?.minutesWorked) {
                    return (
                        <Typography.Text type="secondary">
                            Not submitted
                        </Typography.Text>
                    );
                }

                const totalMinutesSubmitted =
                    (minutesWorked || 0) +
                    (shift.onCallShift?.minutesWorked || 0);
                const payPeriodMinutes =
                    payPeriod &&
                    (payPeriod.minutesWorked || payPeriod.onCallMinutesWorked)
                        ? (payPeriod.minutesWorked || 0) +
                          (payPeriod.onCallMinutesWorked || 0)
                        : null;

                if (
                    payPeriodMinutes &&
                    payPeriodMinutes < totalMinutesSubmitted
                ) {
                    return `${toHourAndMinuteDisplay(payPeriodMinutes, true)} / ${toHourAndMinuteDisplay(totalMinutesSubmitted, true)}`;
                }

                if (payPeriodMinutes) {
                    return toHourAndMinuteDisplay(payPeriodMinutes, true);
                }

                return toHourAndMinuteDisplay(totalMinutesSubmitted, true);
            },
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                submittedMinutesSortValue(a, payPeriodForShift(a)) -
                submittedMinutesSortValue(b, payPeriodForShift(b)),
        },

        {
            title: 'Hours Scheduled',
            width: 140,
            render: (_: any, shift: PayrollShiftListItemDto) => {
                const startTime = DateTime.fromJSDate(shift.startTime);
                const endTime = DateTime.fromJSDate(shift.endTime);
                const scheduledMinutes = endTime.diff(
                    startTime,
                    'minutes'
                ).minutes;
                return toHourAndMinuteDisplay(scheduledMinutes, true);
            },
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                scheduledMinutesSortValue(a) - scheduledMinutesSortValue(b),
        },
        {
            title: 'In Stripe',
            width: 80,
            render: (_: any, shift: PayrollShiftListItemDto) =>
                payPeriodForShift(shift)?.stripeInvoiceId && (
                    <CheckCircleOutlined />
                ),
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                Number(!!payPeriodForShift(a)?.stripeInvoiceId) -
                Number(!!payPeriodForShift(b)?.stripeInvoiceId),
        },
        {
            title: 'In Zeal',
            width: 80,
            render: (_: any, shift: PayrollShiftListItemDto) => {
                if (shift.nurseProfile?.isSalaried) {
                    return (
                        <Typography.Text type="secondary">
                            (Salaried)
                        </Typography.Text>
                    );
                }
                return (
                    payPeriodForShift(shift)?.payrollEmployeeCheckId && (
                        <CheckCircleOutlined />
                    )
                );
            },
            sorter: (a: PayrollShiftListItemDto, b: PayrollShiftListItemDto) =>
                Number(!!payPeriodForShift(a)?.payrollEmployeeCheckId) -
                Number(!!payPeriodForShift(b)?.payrollEmployeeCheckId),
        },
    ];

    return (
        <>
            <ShiftSummary
                shifts={shifts}
                selected={countFilter}
                onSelect={setCountFilter}
            />
            <Card size="small">
                {countFilter && (
                    <Flex align="center" gap={4} style={{ marginBottom: 8 }}>
                        <Text>Showing {filterDescriptions[countFilter]} ·</Text>
                        <Button
                            type="link"
                            onClick={() => setCountFilter(null)}
                            style={{ padding: 0, height: 'auto' }}
                        >
                            Clear filter
                        </Button>
                    </Flex>
                )}
                <AntTable<PayrollShiftListItemDto>
                    size="small"
                    columns={columns}
                    dataSource={filterShifts(shifts, countFilter)}
                    rowKey="id"
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} shifts`,
                    }}
                />
            </Card>
        </>
    );
}
